import "../../styles/LexicalTextEditor.css";
import { useMemo } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TRANSFORMERS } from "@lexical/markdown";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import React from "react";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { MdOutlineTipsAndUpdates } from "react-icons/md";
import Popover from "./Popover";
import { projectTipsData } from "../../data/projects";
import { editorNodes } from "../../utils/lexicalEditorNodes";
import ToolbarPlugin from "../lexical/ToolbarPlugin";
import lexicalEditorTheme from "../../theme/lexicalEditorTheme";
import CodeHighlightPlugin from "../../plugin/lexical/CodeHighlightPlugin";
import ListMaxIndentLevelPlugin from "../../plugin/lexical/ListMaxIndentLevelPlugin";
import AutoLinkPlugin from "../../plugin/lexical/AutoLinkPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import HtmlPlugin from "../../plugin/lexical/HtmlPlugin";
import ImagesPlugin from "../../plugin/lexical/ImagePlugin";

function Placeholder({ placeholder }) {
  return <div className="editor-placeholder">{placeholder}</div>;
}

const LexicalEditor = ({
  containerClass = "w-40  ",
  id,
  onChange = () => {},
  value = "",
  title,
  placeholder,
  disabled = false,
  formik,
  limit,
  onBlur = () => {},
  isContentPreview,
  htmlPreview = false,
  required,
  showImageBtn=false
}) => {
  const editorConfig = useMemo(
    () => ({
      namespace: id,
      theme: lexicalEditorTheme,
      onError(error) {
        throw error;
      },
      editable: !disabled,
      nodes: editorNodes,
    }),
    [disabled, id]
  );

  const error = () => {
    if (formik && formik.touched?.[id] && formik.errors?.[id]) return true;
    else return false;
  };
  return (
    <div className={`${containerClass} flex flex-col font-roboto`}>
      {!isContentPreview && (
        <label
          htmlFor={id}
          id={id}
          className="w-full flex flex-col sm:flex-row justify-between sm:items-center "
        >
          <div className="flex items-center">
            {" "}
            <span className="font-bold text-sm pl-2">{title}</span>
            {required && <span className="text-red-500 ml-1">*</span>}
            {projectTipsData?.[id] && (
              <Popover
                detailElement={projectTipsData?.[id]}
                title={title}
                index={0}
              >
                <MdOutlineTipsAndUpdates className="text-primary ml-1 -mt-2 text-lg mlg:text-xl" />
              </Popover>
            )}
          </div>
          {!disabled && (
            <span
              className={`text-sm min-w-fit ml-2 sm:ml-0 mb-2 sm:mb-0 ${
                +value?.length > +limit
                  ? "text-red-500"
                  : "text-custom-gray-800"
              }  pr-2`}
            >
              {value?.length || 0} / {limit}
            </span>
          )}
        </label>
      )}
      {htmlPreview && disabled ? (
        <div
          dangerouslySetInnerHTML={{ __html: value }}
          className="editor-container mt-3 pl-2"
        />
      ) : (
        <LexicalComposer
          initialConfig={{
            ...editorConfig,
          }}
        >
          <div
            className={`editor-container mt-1 ${!disabled && "border "} ${
              error() ? "border-red-500 !border-2" : "border-custom-gray-800"
            } `}
          >
            {!disabled && <ToolbarPlugin showImageBtn={showImageBtn}  />}
            <div className={`editor-inner ${!disabled && "bg-[#ffffff34]"}`}>
              <RichTextPlugin
                contentEditable={
                  <ContentEditable
                    readOnly={disabled}
                    disabled={disabled}
                    className={`editor-input  ${!disabled && "py-4 px-2.5"}`}
                  />
                }
                placeholder={<Placeholder placeholder={placeholder} />}
                ErrorBoundary={LexicalErrorBoundary}
              />
              <HistoryPlugin />
              <CodeHighlightPlugin />
              <ListPlugin />
              <LinkPlugin />
              <AutoLinkPlugin />
              <ListMaxIndentLevelPlugin maxDepth={7} />
              <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
              <ImagesPlugin captionsEnabled={false} />
              <HtmlPlugin
                onHtmlChanged={(html) => {
                  onChange(html);
                }}
                initialHtml={value}
              />
            </div>
          </div>
        </LexicalComposer>
      )}

      {!isContentPreview && error() && (
        <span className="text-red-500 text-sm mt-1">{formik.errors?.[id]}</span>
      )}
    </div>
  );
};

export default LexicalEditor;
